import { ApolloProvider } from '@apollo/client';
import { AppState, Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import { useEffect, useMemo, useState } from 'react';
import { ToastContainer } from 'react-toastify';

import { ApolloCacheConfig, createAuthorizedApolloClient } from './createAuthorizedApolloClient';

export type { ApolloCacheConfig };

function AuthorizedApolloProvider({
  children,
  graphqlEndpoint,
  auth0ClientId,
  apolloCacheConfig,
}: {
  children: JSX.Element;
  graphqlEndpoint: string;
  auth0ClientId: string;
  apolloCacheConfig?: ApolloCacheConfig;
}) {
  const [accessToken, setAccessToken] = useState('');
  const { isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect, logout } = useAuth0();

  useEffect(() => {
    const getAccessToken = async () => {
      try {
        if (isLoading) return;

        const token = await getAccessTokenSilently({
          authorizationParams: {
            redirect_uri: window.location.origin,
          },
        });

        setAccessToken(token);
      } catch (_err: unknown) {
        // Preserve the path the user landed on so Auth0's post-login
        // bounce-back to `window.location.origin` doesn't drop them at `/`
        // (which would then redirect to their company dashboard).
        await loginWithRedirect({
          appState: {
            returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
          },
        });
      }
    };

    getAccessToken().catch((err) => {
      Sentry.captureException(err);
    });
  }, [accessToken, isLoading, getAccessTokenSilently, loginWithRedirect]);

  const client = createAuthorizedApolloClient({
    graphqlEndpoint,
    apolloCacheConfig,
    getHeaders: () => ({
      Authorization: `Bearer ${accessToken}`,
      'x-original-host': window.location.hostname,
    }),
    onUnauthorized: () => {
      logout({
        clientId: auth0ClientId,
        logoutParams: {
          returnTo: window.location.origin,
        },
      }).catch((err) => {
        Sentry.captureException(err);
      });
    },
  });

  return accessToken && isAuthenticated ? (
    <ApolloProvider client={client}>
      {children}
      {/* All toast messages should be displayed using this single container instance so there aren't multiple containers in the dom */}
      <ToastContainer autoClose={10000} pauseOnHover />
    </ApolloProvider>
  ) : (
    <></>
  );
}

function CookieSessionApolloProvider({
  children,
  graphqlEndpoint,
  apolloCacheConfig,
  onUnauthenticated,
}: {
  children: JSX.Element;
  graphqlEndpoint: string;
  apolloCacheConfig?: ApolloCacheConfig;
  onUnauthenticated: () => void;
}) {
  // No bearer header: the session cookie rides along on `credentials: 'include'`, which the
  // HTTP link already sets. Memoised so that adding state to this component later cannot
  // quietly throw away the Apollo cache on every render.
  const client = useMemo(
    () =>
      createAuthorizedApolloClient({
        graphqlEndpoint,
        apolloCacheConfig,
        getHeaders: () => ({ 'x-original-host': window.location.hostname }),
        onUnauthorized: onUnauthenticated,
      }),
    [graphqlEndpoint, apolloCacheConfig, onUnauthenticated],
  );

  return (
    <ApolloProvider client={client}>
      {children}
      {/* All toast messages should be displayed using this single container instance so there aren't multiple containers in the dom */}
      <ToastContainer autoClose={10000} pauseOnHover />
    </ApolloProvider>
  );
}

function AuthenticationProvider({
  children,
  auth0ClientId,
  auth0Domain,
  auth0Audience,
}: {
  children: JSX.Element;
  auth0Domain: string;
  auth0ClientId: string;
  auth0Audience: string;
}) {
  const onRedirectCallback = (appState?: AppState) => {
    const returnTo = appState?.returnTo;
    // Only restore in-app paths, never an absolute URL, never anything off-origin.
    if (typeof returnTo === 'string' && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
      window.history.replaceState(null, document.title, returnTo);
    }
  };

  return (
    <Auth0Provider
      // useFormData={false}
      domain={auth0Domain}
      clientId={auth0ClientId}
      authorizationParams={{
        audience: auth0Audience,
        redirect_uri: window.location.origin,
      }}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
}

type BaseProps = {
  children: JSX.Element;
  graphqlEndpoint: string;
  apolloCacheConfig?: ApolloCacheConfig;
};

type Auth0ModeProps = BaseProps & {
  mode?: 'auth0';
  auth0Domain: string;
  auth0ClientId: string;
  auth0Audience: string;
};

type CookieModeProps = BaseProps & {
  mode: 'cookie';
  onUnauthenticated: () => void;
};

export type AuthorizedProviderProps = Auth0ModeProps | CookieModeProps;

export function AuthorizedProvider(props: AuthorizedProviderProps) {
  // Reading the discriminant off the object rather than destructuring it is what lets TypeScript
  // narrow the union, and each branch destructures the props it is then known to have.
  // eslint-disable-next-line react/destructuring-assignment -- see above
  if (props.mode === 'cookie') {
    const { children, graphqlEndpoint, apolloCacheConfig, onUnauthenticated } = props;
    return (
      <CookieSessionApolloProvider
        graphqlEndpoint={graphqlEndpoint}
        apolloCacheConfig={apolloCacheConfig}
        onUnauthenticated={onUnauthenticated}
      >
        {children}
      </CookieSessionApolloProvider>
    );
  }

  const { children, graphqlEndpoint, apolloCacheConfig, auth0ClientId, auth0Domain, auth0Audience } = props;
  return (
    <AuthenticationProvider auth0ClientId={auth0ClientId} auth0Domain={auth0Domain} auth0Audience={auth0Audience}>
      <AuthorizedApolloProvider
        graphqlEndpoint={graphqlEndpoint}
        auth0ClientId={auth0ClientId}
        apolloCacheConfig={apolloCacheConfig}
      >
        {children}
      </AuthorizedApolloProvider>
    </AuthenticationProvider>
  );
}
